import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import dayjs from 'dayjs';
import { getCurrentUser } from 'aws-amplify/auth';
import 'react-calendar/dist/Calendar.css';
import '../App.css';

const API_URL = 'https://opmlee572h.execute-api.us-west-2.amazonaws.com';
const DEFAULT_SLOT_DURATION = 30;
const DEFAULT_HOURS = { start: '09:00', end: '17:00' };

function generateSlots(date, start, end, duration) {
  const slots = [];
  let current = dayjs(`${date} ${start}`);
  const endTime = dayjs(`${date} ${end}`);
  while (current.isBefore(endTime)) {
    slots.push(current.format('h:mm A'));
    current = current.add(duration, 'minute');
  }
  return slots;
}

const card = {
  background: 'var(--white)',
  border: '1px solid var(--border)',
  borderRadius: '16px',
  padding: '1.5rem',
  boxShadow: 'var(--shadow-sm)',
};

const labelStyle = {
  fontSize: '0.72rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'var(--text-muted)',
  display: 'block',
  marginBottom: '0.4rem',
};

// ─── PROVIDER AVAILABILITY ───────────────────────────────────────────────────
function ProviderAvailability() {
  const [selectedDate,  setSelectedDate]  = useState(new Date());
  const [blockedDates,  setBlockedDates]  = useState([]);
  const [blockedSlots,  setBlockedSlots]  = useState({});
  const [workingHours,  setWorkingHours]  = useState(DEFAULT_HOURS);
  const [slotDuration,  setSlotDuration]  = useState(DEFAULT_SLOT_DURATION);
  const [saved,         setSaved]         = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [providerId,    setProviderId]    = useState('');

  const dateStr           = dayjs(selectedDate).format('YYYY-MM-DD');
  const slots             = generateSlots(dateStr, workingHours.start, workingHours.end, slotDuration);
  const isDayBlocked      = blockedDates.includes(dateStr);
  const blockedSlotsForDay = blockedSlots[dateStr] || [];
  const totalSlots        = slots.length;
  const blockedCount      = isDayBlocked ? totalSlots : blockedSlotsForDay.length;
  const availableCount    = isDayBlocked ? 0 : totalSlots - blockedCount;

  // Total blocked days this month
  const thisMonth         = dayjs(selectedDate).format('YYYY-MM');
  const blockedThisMonth  = blockedDates.filter(d => d.startsWith(thisMonth)).length;

  useEffect(() => {
    getCurrentUser().then(async (user) => {
      const uid = user.userId;
      setProviderId(uid);
      const res  = await fetch(`${API_URL}/providers?providerId=${uid}`);
      const data = await res.json();
      if (data.availability) {
        setBlockedDates(data.availability.blockedDates || []);
        setBlockedSlots(data.availability.blockedSlots || {});
        setWorkingHours(data.availability.workingHours || DEFAULT_HOURS);
        setSlotDuration(data.availability.slotDuration || DEFAULT_SLOT_DURATION);
      }
    });
  }, []);

  const toggleBlockDay = () => {
    if (isDayBlocked) {
      setBlockedDates(blockedDates.filter(d => d !== dateStr));
    } else {
      setBlockedDates([...blockedDates, dateStr]);
    }
  };

  const toggleBlockSlot = (slot) => {
    const current = blockedSlots[dateStr] || [];
    if (current.includes(slot)) {
      setBlockedSlots({ ...blockedSlots, [dateStr]: current.filter(s => s !== slot) });
    } else {
      setBlockedSlots({ ...blockedSlots, [dateStr]: [...current, slot] });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res      = await fetch(`${API_URL}/providers?providerId=${providerId}`);
      const existing = await res.json();
      await fetch(`${API_URL}/providers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...existing, providerId, availability: { workingHours, slotDuration, blockedDates, blockedSlots } }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) { console.error('Save error:', err); }
    setSaving(false);
  };

  const tileClassName = ({ date }) => {
    const d = dayjs(date).format('YYYY-MM-DD');
    if (blockedDates.includes(d)) return 'blocked-day';
    return null;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

      {/* ── HERO BANNER ── */}
      <div style={{ background: 'var(--green)', borderRadius: '20px', padding: '2.5rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 280, height: 280, background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -50, left: -50, width: 200, height: 200, background: 'radial-gradient(circle, rgba(74,158,107,0.45) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 1, display: 'grid', gridTemplateColumns: '1fr auto', gap: '1.5rem', alignItems: 'center' }}>
          <div>
            {/* Status pill */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', color: 'white', borderRadius: '50px', fontSize: '0.72rem', fontWeight: 700, padding: '0.25rem 0.875rem', marginBottom: '0.875rem', letterSpacing: '0.06em' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: availableCount > 0 ? '#4ade80' : '#f87171', display: 'inline-block', boxShadow: `0 0 6px ${availableCount > 0 ? '#4ade80' : '#f87171'}` }} />
              {availableCount > 0 ? `${availableCount} SLOTS OPEN TODAY` : 'TODAY FULLY BLOCKED'}
            </div>
            <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.6rem, 3vw, 2.1rem)', fontWeight: 800, color: 'white', lineHeight: 1.15, marginBottom: '0.5rem' }}>
              Your Schedule
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.9rem', marginBottom: '1.25rem' }}>
              {dayjs(selectedDate).format('dddd, MMMM D, YYYY')} · {workingHours.start} – {workingHours.end}
            </p>
            {/* Save button in hero */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
              <button
                onClick={handleSave} disabled={saving}
                style={{ background: 'white', color: 'var(--green)', border: 'none', borderRadius: '9px', padding: '0.75rem 1.75rem', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.2s', opacity: saving ? 0.7 : 1 }}
                onMouseEnter={e => { if (!saving) e.currentTarget.style.background = 'var(--cream)'; }}
                onMouseLeave={e => e.currentTarget.style.background = 'white'}
              >{saving ? 'Saving...' : '✓ Save Availability'}</button>
              {saved && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#a8dfb8', fontSize: '0.875rem', fontWeight: 600 }}>
                  <span style={{ width: 18, height: 18, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', color: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem' }}>✓</span>
                  Saved!
                </span>
              )}
            </div>
          </div>

          {/* Right: stat tiles */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem', flexShrink: 0 }}>
            {[
              { label: 'Available Today', value: availableCount },
              { label: 'Blocked Today',   value: blockedCount },
              { label: 'Days Off (Mo)',    value: blockedThisMonth },
            ].map((s, i) => (
              <div key={i} style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '12px', padding: '0.75rem 1.25rem', textAlign: 'center', minWidth: '110px' }}>
                <p style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.55)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.2rem' }}>{s.label}</p>
                <p style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.4rem', fontWeight: 800, color: i === 1 && s.value > 0 ? '#fde68a' : 'white', lineHeight: 1 }}>{s.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── MAIN GRID ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '1.25rem', alignItems: 'start' }}>

        {/* Left — calendar + slot manager */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

          {/* Working hours card */}
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1.25rem' }}>
              <div style={{ width: 36, height: 36, borderRadius: '10px', background: 'var(--green-pale)', border: '1px solid rgba(26,92,58,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>⏰</div>
              <div>
                <p style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text)' }}>Working Hours & Slot Duration</p>
                <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Set when clients can book you</p>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={labelStyle}>Start Time</label>
                <input type="time" value={workingHours.start} onChange={e => setWorkingHours({ ...workingHours, start: e.target.value })}
                  style={{ width: '100%', background: 'var(--cream)', border: '1.5px solid var(--border)', borderRadius: '9px', padding: '0.7rem 0.875rem', color: 'var(--text)', fontSize: '0.9rem', outline: 'none', fontFamily: 'DM Sans, sans-serif', cursor: 'pointer' }}
                  onFocus={e => { e.target.style.borderColor = 'var(--green)'; e.target.style.boxShadow = '0 0 0 3px rgba(26,92,58,0.1)'; }}
                  onBlur={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
              <div>
                <label style={labelStyle}>End Time</label>
                <input type="time" value={workingHours.end} onChange={e => setWorkingHours({ ...workingHours, end: e.target.value })}
                  style={{ width: '100%', background: 'var(--cream)', border: '1.5px solid var(--border)', borderRadius: '9px', padding: '0.7rem 0.875rem', color: 'var(--text)', fontSize: '0.9rem', outline: 'none', fontFamily: 'DM Sans, sans-serif', cursor: 'pointer' }}
                  onFocus={e => { e.target.style.borderColor = 'var(--green)'; e.target.style.boxShadow = '0 0 0 3px rgba(26,92,58,0.1)'; }}
                  onBlur={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
              <div>
                <label style={labelStyle}>Slot Duration</label>
                <select value={slotDuration} onChange={e => setSlotDuration(Number(e.target.value))}
                  style={{ width: '100%', background: 'var(--cream)', border: '1.5px solid var(--border)', borderRadius: '9px', padding: '0.7rem 0.875rem', color: 'var(--text)', fontSize: '0.9rem', outline: 'none', fontFamily: 'DM Sans, sans-serif', cursor: 'pointer' }}
                  onFocus={e => e.target.style.borderColor = 'var(--green)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border)'}
                >
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={90}>1.5 hours</option>
                  <option value={120}>2 hours</option>
                </select>
              </div>
            </div>
          </div>

          {/* Calendar card */}
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1.25rem' }}>
              <div style={{ width: 36, height: 36, borderRadius: '10px', background: 'var(--green-pale)', border: '1px solid rgba(26,92,58,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>📅</div>
              <div>
                <p style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text)' }}>Select a Date to Manage</p>
                <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Click a date to block/unblock individual slots</p>
              </div>
            </div>
            <Calendar onChange={setSelectedDate} value={selectedDate} tileClassName={tileClassName} />
            <div style={{ display: 'flex', gap: '1.25rem', marginTop: '0.875rem', flexWrap: 'wrap' }}>
              {[
                { dot: 'var(--green-light)', label: 'Available' },
                { dot: '#f87171',            label: 'Fully blocked' },
                { dot: 'var(--green)',        label: 'Selected day', border: '2px solid var(--green-dark)' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: item.dot, border: item.border }} />
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Slot manager card */}
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <div style={{ width: 36, height: 36, borderRadius: '10px', background: isDayBlocked ? 'rgba(220,38,38,0.08)' : 'var(--green-pale)', border: `1px solid ${isDayBlocked ? 'rgba(220,38,38,0.2)' : 'rgba(26,92,58,0.15)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                  {isDayBlocked ? '🚫' : '🗓️'}
                </div>
                <div>
                  <p style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text)' }}>{dayjs(selectedDate).format('MMMM D, YYYY')}</p>
                  <p style={{ fontSize: '0.75rem', color: isDayBlocked ? '#dc2626' : 'var(--text-muted)' }}>
                    {isDayBlocked ? 'Fully blocked — no clients can book' : `${availableCount} of ${totalSlots} slots available`}
                  </p>
                </div>
              </div>
              <button onClick={toggleBlockDay}
                style={{ background: isDayBlocked ? 'var(--green-pale)' : 'rgba(220,38,38,0.06)', color: isDayBlocked ? 'var(--green)' : '#dc2626', border: `1.5px solid ${isDayBlocked ? 'rgba(26,92,58,0.2)' : 'rgba(220,38,38,0.25)'}`, borderRadius: '8px', padding: '0.5rem 1rem', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                onMouseEnter={e => { e.currentTarget.style.background = isDayBlocked ? 'var(--green-tint)' : 'rgba(220,38,38,0.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = isDayBlocked ? 'var(--green-pale)' : 'rgba(220,38,38,0.06)'; }}
              >
                {isDayBlocked ? '✓ Unblock Whole Day' : '⊘ Block Whole Day'}
              </button>
            </div>

            {isDayBlocked ? (
              <div style={{ background: 'rgba(220,38,38,0.04)', border: '1px solid rgba(220,38,38,0.15)', borderRadius: '12px', padding: '2rem', textAlign: 'center' }}>
                <p style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>🚫</p>
                <p style={{ fontWeight: 700, color: '#dc2626', marginBottom: '0.25rem', fontSize: '0.9rem' }}>Day fully blocked</p>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Clients cannot book on this day. Click "Unblock Whole Day" to re-open it.</p>
              </div>
            ) : (
              <>
                <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '1rem', lineHeight: 1.5 }}>
                  Click a slot to toggle it. <span style={{ color: 'var(--green)', fontWeight: 600 }}>Green = available</span> for clients to book. <span style={{ color: 'var(--text-light)', fontWeight: 600 }}>Gray = blocked</span>.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {slots.map((slot) => {
                    const isBlocked = blockedSlotsForDay.includes(slot);
                    return (
                      <button key={slot} onClick={() => toggleBlockSlot(slot)}
                        style={{ background: isBlocked ? 'var(--cream-mid)' : 'var(--green-pale)', border: `1.5px solid ${isBlocked ? 'var(--border)' : 'rgba(26,92,58,0.25)'}`, color: isBlocked ? 'var(--text-light)' : 'var(--green)', borderRadius: '8px', padding: '0.4rem 0.875rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.03)'}
                        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        <span style={{ fontSize: '0.65rem' }}>{isBlocked ? '○' : '●'}</span>
                        {slot}
                      </button>
                    );
                  })}
                </div>
                {slots.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                    No slots generated — check your working hours above
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right — summary + tips */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', position: 'sticky', top: '80px' }}>

          {/* Day summary */}
          <div style={{ ...card, background: 'var(--green)', color: 'white', border: 'none' }}>
            <p style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', opacity: 0.65, marginBottom: '0.875rem' }}>
              {dayjs(selectedDate).format('dddd, MMM D')}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
              <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '10px', padding: '0.875rem', textAlign: 'center' }}>
                <p style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.6rem', fontWeight: 800 }}>{availableCount}</p>
                <p style={{ fontSize: '0.72rem', opacity: 0.7, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '0.2rem' }}>Available</p>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '10px', padding: '0.875rem', textAlign: 'center' }}>
                <p style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.6rem', fontWeight: 800 }}>{blockedCount}</p>
                <p style={{ fontSize: '0.72rem', opacity: 0.7, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '0.2rem' }}>Blocked</p>
              </div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.8rem', opacity: 0.75 }}>Working hours</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 700 }}>{workingHours.start} – {workingHours.end}</span>
            </div>
          </div>

          {/* Blocked dates overview */}
          <div style={card}>
            <p style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--text)', marginBottom: '0.875rem' }}>Blocked Days This Month</p>
            {blockedDates.filter(d => d.startsWith(dayjs(selectedDate).format('YYYY-MM'))).length === 0 ? (
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No fully blocked days this month</p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {blockedDates
                  .filter(d => d.startsWith(dayjs(selectedDate).format('YYYY-MM')))
                  .sort()
                  .map(d => (
                    <span key={d} onClick={() => setSelectedDate(new Date(d + 'T12:00:00'))}
                      style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)', color: '#dc2626', borderRadius: '7px', padding: '0.25rem 0.625rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(220,38,38,0.14)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(220,38,38,0.08)'}
                    >{dayjs(d).format('MMM D')}</span>
                  ))
                }
              </div>
            )}
          </div>

          {/* Tips */}
          <div style={{ ...card, background: 'var(--cream-dark)' }}>
            <p style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--text)', marginBottom: '0.875rem' }}>💡 Availability Tips</p>
            {[
              { icon: '🕐', tip: 'Keep your hours consistent so clients know when to expect you online' },
              { icon: '📅', tip: "Block days off in advance — clients can't book blocked slots" },
              { icon: '⚡', tip: 'Shorter slot durations = more bookings per day' },
              { icon: '🔔', tip: 'Save your availability so it appears to clients immediately' },
            ].map((t, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.6rem', marginBottom: i < 3 ? '0.625rem' : 0 }}>
                <span style={{ fontSize: '0.9rem', flexShrink: 0 }}>{t.icon}</span>
                <p style={{ fontSize: '0.78rem', color: 'var(--text-mid)', lineHeight: 1.55 }}>{t.tip}</p>
              </div>
            ))}
          </div>

          {/* Sticky save */}
          <button onClick={handleSave} disabled={saving}
            style={{ background: 'var(--green)', color: 'white', border: 'none', borderRadius: '10px', padding: '0.875rem', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.2s', width: '100%', opacity: saving ? 0.7 : 1 }}
            onMouseEnter={e => { if (!saving) e.currentTarget.style.background = 'var(--green-dark)'; }}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--green)'}
          >{saving ? 'Saving...' : '✓ Save Availability'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── CLIENT BOOKING PICKER ───────────────────────────────────────────────────
export function ClientBookingPicker({ provider, clientId, onConfirm, onCancel }) {
  const [selectedDate,   setSelectedDate]   = useState(new Date());
  const [bookedSlots,    setBookedSlots]    = useState([]);
  const [selectedSlot,   setSelectedSlot]   = useState(null);
  const [selectedService,setSelectedService]= useState(null);
  const [loading,        setLoading]        = useState(false);

  const availability     = provider.availability || {};
  const workingHours     = availability.workingHours || DEFAULT_HOURS;
  const slotDuration     = availability.slotDuration || DEFAULT_SLOT_DURATION;
  const blockedDates     = availability.blockedDates || [];
  const blockedSlots     = availability.blockedSlots || {};
  const dateStr          = dayjs(selectedDate).format('YYYY-MM-DD');
  const isDayBlocked     = blockedDates.includes(dateStr);
  const blockedSlotsForDay = blockedSlots[dateStr] || [];
  const allSlots         = generateSlots(dateStr, workingHours.start, workingHours.end, slotDuration);
  const availableSlots   = allSlots.filter(s => !blockedSlotsForDay.includes(s));

  useEffect(() => {
    fetch(`${API_URL}/bookings?providerId=${provider.providerId}`)
      .then(res => res.json())
      .then(data => setBookedSlots(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [provider.providerId]);

  const isSlotBooked = (slot) => bookedSlots.some(b => b.date === dateStr && b.slot === slot);

  const handlePay = async () => {
    if (!selectedSlot || !selectedService) return;
    setLoading(true);
    try {
      const res  = await fetch(`${API_URL}/create-checkout-session`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ providerId: provider.providerId, clientId, date: dateStr, slot: selectedSlot, service: selectedService, providerName: provider.name || provider.providerId }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (err) { console.error(err); alert('Something went wrong. Please try again.'); }
    setLoading(false);
  };

  const tileDisabled = ({ date }) => {
    const d = dayjs(date).format('YYYY-MM-DD');
    return blockedDates.includes(d) || dayjs(date).isBefore(dayjs(), 'day');
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,26,24,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem', overflowY: 'auto', backdropFilter: 'blur(4px)' }}>
      <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '20px', padding: '2rem', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg)' }}>
        <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.3rem', fontWeight: 800, color: 'var(--text)', marginBottom: '0.25rem' }}>Book with {provider.name}</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem', fontSize: '0.875rem' }}>{provider.serviceType} • {provider.location}</p>
        <p style={{ ...labelStyle, marginBottom: '0.75rem' }}>Select a Service</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
          {provider.services?.map((svc, i) => (
            <div key={i} onClick={() => setSelectedService(svc)} style={{ padding: '0.875rem 1.1rem', border: `1.5px solid ${selectedService?.name === svc.name ? 'var(--green)' : 'var(--border)'}`, borderRadius: '10px', cursor: 'pointer', background: selectedService?.name === svc.name ? 'var(--green-pale)' : 'var(--cream)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'all 0.15s' }}>
              <span style={{ fontWeight: 600, color: 'var(--text)', fontSize: '0.9rem' }}>{svc.name} <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 400 }}>({svc.duration} min)</span></span>
              <span style={{ color: 'var(--green)', fontWeight: 800 }}>${svc.price}</span>
            </div>
          ))}
        </div>
        <p style={{ ...labelStyle, marginBottom: '0.75rem' }}>Pick a Date</p>
        <div style={{ marginBottom: '1.5rem' }}><Calendar onChange={setSelectedDate} value={selectedDate} tileDisabled={tileDisabled} minDate={new Date()} /></div>
        <p style={{ ...labelStyle, marginBottom: '0.75rem' }}>Pick a Time — {dayjs(selectedDate).format('MMM D, YYYY')}</p>
        {isDayBlocked ? (
          <p style={{ color: '#dc2626', marginBottom: '1.5rem', fontSize: '0.875rem' }}>Provider is unavailable on this day.</p>
        ) : availableSlots.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem', fontSize: '0.875rem' }}>No available slots for this day.</p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.5rem' }}>
            {availableSlots.map((slot) => {
              const isBooked = isSlotBooked(slot);
              return (
                <button key={slot} onClick={() => { if (!isBooked) setSelectedSlot(slot); }} disabled={isBooked}
                  style={{ background: isBooked ? 'var(--cream-mid)' : selectedSlot === slot ? 'var(--green)' : 'var(--green-pale)', border: `1.5px solid ${isBooked ? 'var(--border)' : selectedSlot === slot ? 'var(--green)' : 'rgba(26,92,58,0.25)'}`, color: isBooked ? 'var(--text-light)' : selectedSlot === slot ? 'white' : 'var(--green)', borderRadius: '8px', padding: '0.4rem 0.875rem', fontSize: '0.8rem', fontWeight: 600, cursor: isBooked ? 'not-allowed' : 'pointer', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.15s' }}>
                  {slot}
                </button>
              );
            })}
          </div>
        )}
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn-secondary" style={{ flex: 1 }} onClick={onCancel}>Cancel</button>
          <button className="btn-primary" style={{ flex: 1 }} onClick={handlePay} disabled={!selectedService || !selectedSlot || loading}>
            {loading ? 'Loading...' : selectedService && selectedSlot ? `Pay $${selectedService.price}` : 'Select service & time'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN EXPORT ─────────────────────────────────────────────────────────────
function BookingCalendar({ isProvider = false }) {
  if (isProvider) return <ProviderAvailability />;
  return null;
}

export default BookingCalendar;